import { Grid, IconButton, styled, TextField, Typography } from "@mui/material";
import { TrashIcon } from "@phosphor-icons/react/dist/csr/Trash";
import { useState } from "react";
import { Control, Controller, ControllerProps, FieldValues, Path } from "react-hook-form";
import Permissions from "../../../constants/permissions";
import { IfPermission } from "../../Permissions";
import { KbdShortcut } from "../../ui/Kbd";

interface IEditableField<T extends FieldValues> {
  helperText: string;
  name: Path<T>;
  onEnter?: () => void;
  label: string;
  onClickDelete?: () => void;
  permission?: Permissions;
  dataCy: string;
  key: string;
  control: Control<T>;
  rules?: ControllerProps<T>["rules"];
  error?: boolean;
  onEscape?: () => void;
  handleBlur?: () => void;
  focusable?: boolean;
}

const ContainerGrid = styled(Grid)({
  "&:hover .hide": {
    display: "block",
  },

  ".hide": {
    display: "none",
  },
});

const EditableField = <T extends FieldValues>({
  helperText,
  name,
  label,
  onEnter,
  onClickDelete,
  permission,
  dataCy,
  control,
  rules,
  error,
  onEscape,
  handleBlur,
  focusable,
}: IEditableField<T>) => {
  const [focused, setFocused] = useState(false);
  const handleKeyDown = async (e: React.KeyboardEvent<HTMLDivElement>) => {
    switch (e.key) {
      case "Enter":
        e.preventDefault();
        if (onEnter) {
          onEnter();
        }

        break;
      case "Escape":
        setFocused(false);
        if (onEscape) {
          onEscape();
        }
        break;
      default:
        break;
    }
  };

  return (
    <Controller
      key={name}
      control={control}
      name={name}
      rules={rules}
      render={({ field: { onChange, value } }) => (
        <ContainerGrid container className="myDIV" sx={{ alignItems: "center" }}>
          <Grid size={8}>
            <TextField
              value={value}
              onChange={(e) => {
                setFocused(true);
                onChange(e);
              }}
              variant="outlined"
              label={label}
              name={name}
              data-cy={dataCy}
              autoComplete="off"
              style={{
                width: "100%",
              }}
              onKeyDown={handleKeyDown}
              error={error}
              helperText={helperText || ""}
              onBlur={() => {
                if (handleBlur) {
                  handleBlur();
                }
                setFocused(false);
              }}
              onFocus={() => {
                setFocused(true);
              }}
              inputRef={(input) => (focusable ? input?.focus() : null)}
            />
          </Grid>
          {permission ? (
            <IfPermission permission={permission}>
              <Grid size={4}>
                {focused ? (
                  <Typography sx={{ marginLeft: "10px", fontSize: "9px" }}>
                    Press <KbdShortcut>esc</KbdShortcut> to cancel; <KbdShortcut>enter</KbdShortcut> to save
                  </Typography>
                ) : (
                  <IconButton onClick={onClickDelete} data-cy="delete-icon" className="hide">
                    <TrashIcon />
                  </IconButton>
                )}
              </Grid>
            </IfPermission>
          ) : null}
        </ContainerGrid>
      )}
    />
  );
};

export { EditableField };
